import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface EventListItem {
  event_id: number
  author_id: number | null
  title: string
  created_at: string
  thumbnail_url: string | null
}

const PAGE_SIZE = 6

export default function EventList() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<EventListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTitle, setSearchTitle] = useState('')
  const [inputTitle, setInputTitle] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    fetchEvents()
  }, [searchTitle, page])

  // 검색 시 1페이지로 리셋
  const handleSearch = () => {
    setPage(1)
    setSearchTitle(inputTitle)
  }

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (searchTitle) params.append('title', searchTitle)
      params.append('skip', String((page - 1) * PAGE_SIZE))
      params.append('limit', String(PAGE_SIZE))

      const res = await fetch(getApiUrl(`/marubit/api/event/list?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setEvents(data.events)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  const totalPages = Math.ceil(total / PAGE_SIZE)

  // 페이지 번호 배열 생성 (최대 5개)
  const getPageNumbers = () => {
    const start = Math.max(1, page - 2)
    const end = Math.min(totalPages, start + 4)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">행사 목록</h1>
            <p className="text-[14px] text-gray-400">총 {total}건</p>
          </div>
          {/* <button
            onClick={() => navigate('/event/register')}
            className="px-5 py-2 bg-[#887267] text-white text-[14px] font-bold rounded-lg hover:bg-[#6b5248] transition-colors"
          >
            + 행사 등록
          </button> */}
        </div>



        {/* 목록 */}
        {events.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-300 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            등록된 행사가 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {events.map(event => (
              <div
                key={event.event_id}
                onClick={() => navigate(`/event/${event.event_id}`)}
                className="bg-white rounded-2xl overflow-hidden f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] cursor-pointer hover:shadow-[0_4px_20px_rgba(112,48,160,0.15)] hover:-translate-y-1 transition-all p-4"
              >
                <div className="w-full aspect-[3/4] bg-gray-50 border border-gray-100 rounded-xl overflow-hidden flex items-center justify-center mb-3">
                  {event.thumbnail_url ? (
                    <img
                      src={event.thumbnail_url}
                      alt={event.title}
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-300">
                      <svg viewBox="0 0 24 24" width={32} height={32} fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span className="text-[12px]">이미지 없음</span>
                    </div>
                  )}
                </div>
                <p className="text-[14px] font-bold text-[#222] line-clamp-2 mb-1">{event.title}</p>
                <p className="text-[11px] text-gray-400">{formatDate(event.created_at)}</p>
              </div>
            ))}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8 mb-6">
            {/* 이전 */}
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ‹
            </button>

            {/* 페이지 번호 */}
            {getPageNumbers().map(num => (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={[
                  'w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-medium transition-colors',
                  num === page
                    ? 'bg-[#887267] text-white border border-[#887267]'
                    : 'border border-gray-200 text-gray-500 hover:border-[#887267] hover:text-[#887267]',
                ].join(' ')}
              >
                {num}
              </button>
            ))}

            {/* 다음 */}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ›
            </button>
          </div>
        )}

                {/* 검색 */}
        <div className="bg-white rounded-2xl p-5 mt-6 max-w-[600px] mx-auto f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] flex gap-3">
          <input
            type="text"
            placeholder="행사명 검색"
            value={inputTitle}
            onChange={e => setInputTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="flex-1 px-4 py-2 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
          />
          <button
            onClick={handleSearch}
            className="px-5 py-2 bg-[#887267] text-white text-[14px] font-bold rounded-lg hover:bg-[#6b5248] transition-colors"
          >
            검색
          </button>
        </div>
      </div>
    </div>
  )
}